<template>
    <framework>
        <template #side>
            <side-panel-nav />
        </template>
        <template #main>
            <main-header title="Cart Checkout" icon="fa-shopping-cart" />

            <div v-if="loading" class="has-text-white mt-6">
                <i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Loading your cart...
            </div>

            <div v-else-if="!quote.id" class="has-text-white mt-6">
                <p><i class="fas fa-info-circle mr-2"></i>No cart found. You may have already completed checkout or the
                    cart may have been removed.</p>
                <router-link to="/dashboard" class="button is-primary mt-4">Return to Dashboard</router-link>
            </div>

            <div v-else>
                <!-- Courses Header -->
                <div class="content mt-6">
                    <h4 class="has-text-white mb-4">Courses ({{ quote.courses ? quote.courses.length : 0 }})</h4>
                </div>

                <!-- Individual Course Cards -->
                <div v-if="quote.courses && quote.courses.length">
                    <cart-course v-for="(course, index) in coursesWithDetails" :key="index" :course="course"
                        class="mb-4" />
                </div>

                <!-- Checkout Header -->
                <div class="content mt-6 pt-6">
                    <div class="columns is-gapless">
                        <div class="column is-narrow has-text-white is-hidden-mobile"><i
                                class="far fa-arrow-right-long fa-2x mr-5"></i></div>
                        <div class="column">
                            <h1 class="has-text-white mb-2">Checkout</h1>
                        </div>
                    </div>
                </div>

                <!-- Checkout Form -->
                <div class="cart-card">
                    <section v-if="!completed">
                        <div class="content">

                            <!-- Terms and Confirmations -->
                            <h4 class="mb-4">Terms and Acknowledgments</h4>


                            <b-message type="is-info" class="mt-5">


                                <div class="columns is-gapless mb-4">
                                    <div class="column is-narrow">
                                        <b-switch type="is-success" class="mr-4"
                                            v-model="ready.courseDetails"></b-switch>
                                    </div>
                                    <div class="column">
                                        I have reviewed all course details above, including course selection, subject, level, clock hours, and confirm that all information is accurate.
                                    </div>
                                </div>

                                <div class="columns is-gapless mb-4">
                                    <div class="column is-narrow">
                                        <b-switch type="is-success" class="mr-4"
                                            v-model="ready.refundPolicy"></b-switch>
                                    </div>
                                    <div class="column">
                                        I understand that these courses are <u>non-refundable</u> and non-transferable,
                                        regardless if courses have been started, completed or not.
                                    </div>
                                </div>
                                <div class="columns is-gapless mb-4">
                                    <div class="column is-narrow">
                                        <b-switch type="is-success" class="mr-4" v-model="ready.terms"></b-switch>
                                    </div>
                                    <div class="column">
                                        I understand that these courses will expire 3 years from the date of enrollment (listed above). An additional fee may be assessed to reactivate any course that has expired.
                                    </div>
                                </div>
                            </b-message>


                            <!-- Signature -->
                            <h4 class="mt-6 mb-4">Signature</h4>
                            <div class="mb-4">
                                <p><i class="far fa-file-signature mr-3"></i>By signing below, I confirm that I have
                                    read and agree to all terms and conditions stated above. I authorize the charge to
                                    my payment method for the total amount shown.</p>
                            </div>

                            <div class="signature-container">
                                <div class="signature-pad">
                                    <div class="sig-line"></div>
                                    <div class="sig-x">
                                        <b-icon icon="times" />
                                    </div>
                                    <div class="sig-name" v-if="currentUser">{{ currentUser.first_name }} {{currentUser.last_name }}</div>
                                    <div class="sig-buttons">
                                        <b-button size="is-small" @click="undoSignature" class="mr-2">
                                            <b-icon icon="undo" class="mr-1" />
                                            Undo
                                        </b-button>
                                        <b-button size="is-small" @click="clearSignature">
                                            <b-icon icon="eraser" class="mr-1" />
                                            Clear
                                        </b-button>
                                    </div>
                                    <vue-signature class="sig" ref="signature" :sigOption="signatureOption" :w="'1000px'" :h="'180px'" />
                                </div>
                            </div>

                            <hr />
                            <!-- Payment -->
                            <h4 class="mt-6 mb-4">Payment</h4>

                            <div v-if="!clientSecret" class="pb-3"><i
                                    class="fas fa-spin fa-spinner fa-fw mr-3"></i>Establishing secure connection for
                                Credit Card...</div>

                            <div v-show="clientSecret" class="pb-3">

                                <div class="columns is-gapless">
                                    <div class="column is-narrow"><i class="far fa-info-circle mr-4"></i></div>
                                    <div class="column">
                                        <p>Your card will be charged <strong>{{ baseAmount | currency }}</strong> for
                                            these course enrollments.</p>

                                        <!-- Tax breakdown -->
                                        <div v-if="taxAmount > 0" class="mt-5 mb-4"
                                            style="background-color: #f8f9fa; padding: 1rem; border-radius: 4px; border: 1px solid #e0e2e8;">
                                            <table style="width: 100%; font-size: 0.95rem; border-collapse: collapse;">
                                                <tr style="font-weight: 600;">
                                                    <td style="padding-bottom: 0.5rem; border: none;">Total:</td>
                                                    <td style="text-align: right; padding-bottom: 0.5rem; border: none;">
                                                        {{ baseAmount | currency }}</td>
                                                </tr>
                                                <tr v-if="taxDetails" style="font-size: 0.85rem; color: #6c757d;">
                                                    <td colspan="2" style="padding-top: 0.5rem; border: none; border-top: 1px solid #dee2e6;">
                                                        <i class="fas fa-info-circle mr-2"></i>Includes {{ taxAmount | currency }} sales tax ({{(taxDetails.taxRate * 100).toFixed(2) }}%, {{taxDetails.jurisdiction }})
                                                    </td>
                                                </tr>
                                                <tr v-else-if="taxAmount > 0" style="font-size: 0.85rem; color: #6c757d;">
                                                    <td colspan="2" style="padding-top: 0.5rem; border: none; border-top: 1px solid #dee2e6;">
                                                        <i class="fas fa-info-circle mr-2"></i>Includes {{ taxAmount | currency }} sales tax
                                                    </td>
                                                </tr>
                                            </table>
                                        </div>

                                        <div class="mt-3">
                                            <div class="card-container">
                                                <div ref="card"></div>
                                                <b-message type="is-danger" v-show="stripeErrors" class="mt-6">
                                                    <div id="card-errors"><i
                                                            class="far fa-exclamation-triangle mr-3"></i>{{ stripeErrors}}</div>
                                                </b-message>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <hr class="mt-6" />

                            <b-button size="is-large" type="is-primary" expanded class="mt-6" @click="checkout"
                                :disabled="!isReadyForPayment || processing">
                                <i v-if="!processing" class="fas fa-check mr-4"></i>
                                <i v-else class="fas fa-spin fa-spinner mr-4"></i>
                                {{ processing ? 'Processing Payment...' : 'Submit Payment' }}
                            </b-button>

                            <div v-if="!isReadyForPayment" class="mt-4"
                                style="background-color: #fff3cd; border: 1px solid #ffc107; border-radius: 4px; padding: 1rem;">
                                <div style="font-weight: 600; margin-bottom: 0.5rem; color: #856404;"><i
                                        class="fas fa-exclamation-triangle mr-2"></i>Please complete the following to
                                    submit payment:</div>
                                <ul style="margin: 0; padding-left: 1.5rem; color: #856404;">
                                    <li v-if="!allConfirmationsReady" style="margin-bottom: 0.25rem;">Confirm all terms
                                        above</li>
                                    <li v-if="!signature" style="margin-bottom: 0.25rem;">Sign the agreement above</li>
                                    <li v-if="!ccReady" style="margin-bottom: 0.25rem;">Complete your credit card
                                        information</li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    <section v-else>
                        <div class="pt-4">
                            <div class="columns">
                                <div class="column is-narrow">
                                    <i class="far fa-check-circle fa-3x has-text-primary"></i>
                                </div>
                                <div class="column is-9 content">
                                    <h3 class="mt-3">Thank you for your enrollment.</h3>

                                    <p class="mt-6">You can access your courses from your <strong><i
                                                class="far fa-book-reader mr-2 ml-1"></i>Course Enrollments</strong> at
                                        any time.</p>

                                    <div class="mt-6">
                                        <router-link to="/enrollments">
                                            <b-button type="is-primary">
                                                <span class="is-hidden-mobile">Continue to your <i
                                                        class="far fa-book-reader mr-2 ml-1"></i>Course Enrollments<i
                                                        class="far fa-long-arrow-right ml-3"></i></span>
                                                <span class="is-hidden-desktop"><i
                                                        class="far fa-book-reader mr-2 ml-1"></i>Course Enrollments<i
                                                        class="far fa-long-arrow-right ml-3"></i></span>
                                            </b-button>
                                        </router-link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </template>
    </framework>
</template>

<script>
import { client as http, stripePK } from "../../http_client";
import { mapGetters } from "vuex";
import async from "async";
import moment from "moment";
import Framework from "../../components/framework/Framework";
import SidePanelNav from "../../components/framework/SidePanelNav";
import MainHeader from "../../components/framework/MainHeader";
import UiCard from "../../components/UiCard";
import VueSignature from 'vue-signature';
import CartCourse from "../../components/CartCourse";

let stripe = Stripe(stripePK),
    elements = stripe.elements(),
    card = undefined;

export default {
    components: { Framework, SidePanelNav, MainHeader, UiCard, VueSignature, CartCourse },
    data() {
        let _this = this;
        return {
            loading: true,
            processing: false,
            completed: false,
            quote: {},
            clientSecret: null,
            intentId: null,
            taxAmount: 0,
            taxDetails: null,
            intentCharge: 0,
            ccReady: false,
            stripeErrors: '',
            signature: null,
            ready: {
                courseDetails: false,
                refundPolicy: false,
                terms: false
            },
            signatureOption: {
                penColor: 'rgb(0, 0, 0)',
                backgroundColor: 'rgb(255, 255, 255)',
                onEnd: function () {
                    _this.saveSignature();
                }
            }
        };
    },
    computed: {
        ...mapGetters(['currentUser']),
        baseAmount: function () {
            return parseFloat(this.quote.total) || 0;
        },
        chargeAmount: function () {
            // Use the intent charge if available (includes tax)
            if (this.intentCharge) {
                return (this.intentCharge / 100);
            }
            // Fallback to base + tax
            return this.baseAmount + this.taxAmount;
        },
        coursesWithDetails: function () {
            if (!this.quote.courses) return [];

            return this.quote.courses.map(course => ({
                name: course.course_name || 'Course',
                custom1: course.custom1 || '',
                custom2: course.custom2 || '',
                hours: course.hours || 0,
                expiresOn: moment().add(3, 'years').format('MMMM D, YYYY')
            }));
        },
        allConfirmationsReady: function () {
            return this.ready.courseDetails && this.ready.refundPolicy && this.ready.terms;
        },
        isReadyForPayment: function () {
            return this.ccReady && this.allConfirmationsReady && this.signature !== null;
        }
    },
    methods: {
        loadCart() {
            this.loading = true;
            http.get('app/courses/cart').then(response => {
                this.quote = response.data;

                // Set tax details if available
                if (this.quote.tax_amount) {
                    this.taxAmount = parseFloat(this.quote.tax_amount);
                }
                if (this.quote.tax_rate) {
                    this.taxDetails = {
                        taxRate: parseFloat(this.quote.tax_rate),
                        stateRate: parseFloat(this.quote.tax_state_rate) || 0,
                        localRate: parseFloat(this.quote.tax_local_rate) || 0,
                        jurisdiction: this.quote.tax_jurisdiction,
                        locationCode: this.quote.tax_location_code,
                        resultCode: this.quote.tax_result_code
                    };
                }

                this.loading = false;

                // Initialize Stripe after cart is loaded
                this.$nextTick(() => {
                    this.stripeInit();
                });
            }).catch(err => {
                console.error('Error loading cart:', err);
                this.loading = false;
            });
        },
        stripeInit() {
            // Get payment intent
            http.post('app/courses/cart-intent').then(response => {
                this.clientSecret = response.data.clientSecret;
                this.intentId = response.data.id;
                this.intentCharge = response.data.total * 100;
                this.taxAmount = response.data.taxAmount || 0;
                this.taxDetails = response.data.taxDetails || null;

                // Wait for next tick to ensure DOM is ready
                this.$nextTick(() => {
                    if (!this.$refs.card) {
                        console.error('Card ref not found');
                        return;
                    }

                    // Create and mount card element
                    if (card) {
                        card.unmount();
                    }

                    card = elements.create('card', {
                        style: {
                            base: {
                                color: '#32325d',
                                fontSize: '16px',
                                '::placeholder': {
                                    color: '#aab7c4',
                                }
                            },
                            invalid: {
                                color: '#ff3860',
                            }
                        }
                    });
                    card.mount(this.$refs.card);

                    card.on('change', event => {
                        if (event.error) {
                            this.stripeErrors = event.error.message;
                        } else {
                            this.stripeErrors = '';
                        }
                        this.ccReady = !!(event.complete);
                    });
                });
            }).catch(err => {
                console.error('Error creating payment intent:', err);
            });
        },
        checkout() {
            if (this.processing) return;

            this.processing = true;
            this.stripeErrors = '';

            async.series([
                // Confirm card payment
                (cb) => {
                    stripe.confirmCardPayment(this.clientSecret, {
                        payment_method: {
                            card: card
                        }
                    }).then(result => {
                        if (result.error) {
                            this.stripeErrors = result.error.message;
                            cb(result.error);
                        } else {
                            cb();
                        }
                    }).catch(cb);
                },
                // Complete checkout on backend
                (cb) => {
                    http.post('app/courses/cart-checkout', {
                        intent_id: this.intentId,
                        signature: this.signature
                    }).then(response => {
                        this.completed = true;
                        cb();
                    }).catch(cb);
                }
            ], err => {
                if (err) {
                    console.error('Checkout error:', err);
                }
                this.processing = false;
            });
        },
        clearSignature() {
            this.$refs.signature.clear();
            this.signature = null;
        },
        undoSignature() {
            this.$refs.signature.undo();
            this.saveSignature();
        },
        saveSignature() {
            this.signature = this.$refs.signature.save();
        }
    },
    mounted() {
        if (!localStorage.getItem('token')) {
            this.$router.push('/');
            return;
        }
        this.loadCart();
    }
};
</script>

<style lang="scss" scoped>
// White card container for checkout section
.cart-card {
    background-color: white;
    border-radius: 4px;
    box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);
    padding: 3.7rem 3.7rem 6rem;
    margin-bottom: 6rem;

    @media (max-width: 1180px) {
        padding: 2rem;
        margin-top: 0;
        margin-bottom: 4rem;
    }

    @media (max-width: 768px) {
        padding: 1rem;
    }
}

// Signature Container
.signature-container {
    overflow-x: auto;
    overflow-y: hidden;
    max-width: 100%;
}

// Signature Pad
.signature-pad {
    position: relative;
    background: white;
    max-width: 100%;
    margin-top: 2rem;

    .sig {
        border: none;
    }

    .sig-x {
        position: absolute;
        bottom: 45px;
        left: 20px;
        font-size: 1.5rem;
    }

    .sig-name {
        position: absolute;
        bottom: 20px;
        left: 50px;
        font-size: 1rem;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
    }

    .sig-line {
        position: absolute;
        bottom: 45px;
        left: 20px;
        right: 20px;
        border-bottom: 2px solid red;
    }

    .sig-buttons {
        position: absolute;
        top: 10px;
        right: 20px;
    }
}

// Card container for stripe element
.card-container {
    border: 1px solid #e7e7e7;
    border-radius: 8px;
    padding: 2rem;
    margin-bottom: 2rem;
}

.text-muted {
    color: #888;
}

// Required asterisk styling (from Register.vue)
.required {
    color: #ff3860;
}
</style>
